import {
    DateActions,
    IUpdatedGridData,
    TClientTags,
    TCommonRowData,
    TCommonRowObj,
    ActionType,
} from "@/ts/common-types";
import dayjs, { Dayjs } from "dayjs";
import { keys } from "underscore";

const getUniqueTagsToUpdate = (
    existingTags?: TClientTags[] | null,
    newTags?: TClientTags[] | null
): TClientTags[] => {
    const existingIds = new Set((existingTags ?? []).map((tag) => tag.tag_id));
    const uniqueNewTags = (newTags ?? []).filter(
        (tag) => !existingIds.has(tag.tag_id)
    );
    return [...(existingTags ?? []), ...uniqueNewTags];
};  

//* This function is used for operation such as increasing and decreasing bid or budget
const getUpdatedBidOrBudgetValue = (
    changeType: string,
    changeValue: number,
    value: number
) => {
    let result: number;
    switch (changeType) {
        case ActionType.PERC_INC:
            result = value + (value * +changeValue) / 100;
            break;
        case ActionType.PERC_DEC:
            result = value - (value * +changeValue) / 100;
            break;
        case ActionType.ABSOLUTE:
            result = +changeValue;
            break;
        default:
            result = value;
    }
    return Math.round(result * 100) / 100;
};

const parseNumericValue = (value: any): number => {
    if (typeof value === 'number') return value;
    if (!value) return 0;
    return Number(parseFloat(value.toString().replace(/[^0-9.-]+/g, '')));
};

export const getEndDate = (
    record: TCommonRowObj,
    dateChangeType: string,
    {
        selectedDate,
        addNumDays,
    }: {
        selectedDate: string | number;
        addNumDays: number;
    }
) =>
    dateChangeType !== DateActions.NO_END_DATE
        ? dateChangeType === DateActions.ADD_DAYS
            ? dayjs((record.end_date || dayjs()) as Dayjs)
                .add(addNumDays, "days")
                .format('YYYY-MM-DD')
            : dayjs(selectedDate).format('YYYY-MM-DD')
        : 0;


export const getUpdatedGridData = (args: IUpdatedGridData) => {

    /**
     * !Note[DND]
     * * This fn is throwing a error in revstack modules, where all the args are null
     * * But, there's no role of this fn in revstack modules, and the root cause is still not clear
     * * Henceforth, we have added the below condition for args
     */

    if (keys(args).length) {
        const {
            gridData,
            currentRow,
            selectedRows,
            paramToChange,
            paramToFilter,
            newParamValue,
            isRelatedGrid,
            rowIdentifier,
            updateType,
            successfulUpdatedRows,
            additionalValuesToUpdate,
            bulkParamsToChange,
            dateUpdateConfig
        } = args;
        let rowData: TCommonRowData;

        //* For updating relative grids after individual action is performed

        if (isRelatedGrid) {
            rowData = JSON.parse(JSON.stringify(selectedRows)).map(
                (data: TCommonRowObj) => {
                    if (data[paramToFilter] === rowIdentifier) {
                        data[paramToChange] = newParamValue;
                    }
                    return data;
                }
            );
            return rowData;
        }

        //* updating grid when bulk action is performed

        if (selectedRows?.length && gridData?.length) {
            rowData = JSON.parse(JSON.stringify(gridData));
            const filterParamValues = (
                successfulUpdatedRows && successfulUpdatedRows.length
                    ? successfulUpdatedRows
                    : selectedRows
            ).map((selectedRow: TCommonRowObj) => selectedRow[paramToFilter]);
            bulkParamsToChange && bulkParamsToChange.forEach((param) => {
                rowData?.forEach((row: TCommonRowObj) => {
                    if (filterParamValues.includes(row[paramToFilter])) {
                        if (updateType) {
                            row[param.key] = getUpdatedBidOrBudgetValue(
                                updateType,
                                +param.newValue,
                                parseNumericValue(row[param.key])
                            );
                        } else if (param.isDateCalcReq) {
                            row[param.key] = getEndDate(
                                row,
                                param.dateUpdateType,
                                dateUpdateConfig
                            );
                        } else if (param.shouldArrayPreserve) {
                            row[param.key] = getUniqueTagsToUpdate(
                                row[param.key] as TClientTags[],
                                param.newValue as unknown as TClientTags[]
                            );
                        } else {
                            row[param.key] = param.newValue;
                        }
                    }
                });
            });
            return { rowData };
        }

        if (currentRow) {
            rowData = [];
            currentRow[paramToChange] = newParamValue;
            if (Object.keys(additionalValuesToUpdate).length) {
                for (const key in additionalValuesToUpdate) {
                    currentRow[key] = additionalValuesToUpdate[key];
                }
            }
            gridData?.forEach((row: TCommonRowObj) =>
                rowData.push(
                    currentRow[paramToFilter] === row[paramToFilter]
                        ? currentRow
                        : row
                )
            );
            return { rowData, rowIdentifier: currentRow[paramToFilter] };
        }
    }
};

onmessage = (event) => postMessage(getUpdatedGridData(event.data));
